import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Plus, MagnifyingGlass, CaretUp, CaretDown, CaretRight,
    CaretUpDown, ChartDonut, ListBullets, Cube,
} from "@phosphor-icons/react";
import AlignerInsights from "@/pages/doctor/AlignerInsights";
import DemoBadge from "@/components/case/DemoBadge";
// iter90 — DraftCasesCard removed; Drafts now live as a stage bucket
// in the main grid. The component file stays in the codebase for now
// in case we want to revive the standalone card view later.
import { computeResumeStep, computeDraftProgress } from "@/components/case/DraftCasesCard";
import { statusLabel as sharedStatusLabel, statusTone as sharedStatusTone } from "@/lib/caseStatus";

/* Local tone palette used by the Aligner Cases table + stage buckets.
   iter101.0 — Status labels are now sourced from `/lib/caseStatus.js`
   so every doctor surface (Dashboard, Aligner Cases list, Case Detail,
   Patients list) reads the same string. Only the tone mapping stays
   local because this file uses a compact 4-token palette (neutral /
   primary / amber / rose) rather than caseStatus.js's 6-token palette. */
const SHARED_TONE_TO_LOCAL = {
    slate:   "neutral",
    sky:     "primary",
    violet:  "primary",
    emerald: "primary",
    amber:   "amber",
    rose:    "rose",
};
function statusMetaFor(status) {
    const label = sharedStatusLabel(status, "doctor");
    const tone = SHARED_TONE_TO_LOCAL[sharedStatusTone(status)] || "neutral";
    return { label: label || (status || "—"), tone };
}

/* Aligner Dashboard scope: shows the entire patient journey — both
   pre-treatment (planning / QA / approval) and post-delivery (kit / wear /
   refinement). The doctor sees every case here in one place. */
const POST_DELIVERY = ["approved", "in_production", "shipped", "in_treatment", "treatment_started", "refinement_requested", "completed", "assigned_to_production"];
const PRE_DELIVERY = ["draft", "submitted", "in_planning", "submitted_to_qa", "sent_for_approval", "ready_for_review", "info_requested", "revision_requested", "pending_admin_review", "in_qa_review"];

/* Six stage buckets matching the doctor's mental model — workflow milestones
   they speak about with patients/staff, not internal lifecycle statuses.

   `info_requested` rolls into "Submitted for Planning" (planner blocked, still
   their problem). `revision_requested` (pre-treatment) and `refinement_requested`
   (post-treatment) both surface as "In Refinement" — from the doctor's POV both
   = COE is reworking the plan. The Dashboard's Action Needed band handles the
   per-status urgency distinction. */
const STAGE_BUCKETS = [
    {
        // iter90 — Drafts now live as a first-class stage card. Clicking
        // it filters the table to draft cases; rows route back to the
        // wizard instead of the read-only CaseDetail. Removes the prior
        // standalone DraftCasesCard which felt like a separate concept.
        key: "drafts",
        label: "Drafts",
        sub: "Started but not submitted yet",
        tone: "amber",
        match: (c) => c.status === "draft",
    },
    {
        // iter97.88 — Cases the doctor opened via "Book Invisique IO
        // Scanning Service" sit here until the technician visits the
        // clinic and uploads scans. Without this card these cases were
        // invisible whenever a filter was selected; the doctor needs an
        // at-a-glance list of patients whose scan visit is pending so
        // they can chase the appointment if it slips.
        key: "scan-visit-scheduled",
        label: "Scan Visit Scheduled",
        sub: "Awaiting technician visit & scan upload",
        tone: "amber-soft",
        match: (c) => c.status === "scan_visit_scheduled",
    },
    {
        // iter97.88 — Pair to "Scan Visit Scheduled". When the technician
        // completes their visit (Phase A3) the case lands here so the
        // doctor sees "scans are in — resume your case wizard". Primary
        // tone because it's an actionable doctor-blocked state.
        key: "scans-uploaded",
        label: "Scans Uploaded",
        sub: "Technician captured scans — resume your case",
        tone: "primary",
        match: (c) => c.status === "scan_visit_completed",
    },
    {
        key: "submitted-for-planning",
        label: "Plan in Progress",
        sub: "Plan being authored — by you or our COE",
        tone: "primary-soft",
        match: (c) => ["submitted", "in_planning", "submitted_to_qa", "info_requested", "plan_authoring", "pending_admin_review", "in_qa_review"].includes(c.status),
    },
    {
        key: "review-the-plan",
        label: "Review the Plan",
        sub: "Plan ready for your approval",
        tone: "primary",
        match: (c) => ["sent_for_approval", "ready_for_review"].includes(c.status),
    },
    {
        key: "in-refinement",
        label: "In Refinement",
        sub: "Plan being reworked",
        tone: "amber",
        match: (c) => ["revision_requested", "refinement_requested"].includes(c.status),
    },
    {
        key: "submitted-to-lab",
        label: "Submitted to Lab",
        sub: "Manufacturing in progress",
        tone: "primary-soft",
        match: (c) => ["submitted_to_production", "approved", "assigned_to_production", "in_production"].includes(c.status),
    },
    {
        key: "aligners-shipped",
        label: "Aligners Shipped",
        sub: "On the way — confirm receipt when they arrive",
        tone: "primary",
        match: (c) => c.status === "shipped",
    },
    {
        key: "aligners-received",
        label: "Aligners Received",
        sub: "Awaiting in-clinic fitting",
        tone: "amber",
        match: (c) => c.status === "in_treatment",
    },
    {
        key: "treatment-started",
        label: "Treatment Started",
        sub: "Patient is wearing aligners",
        tone: "primary",
        match: (c) => c.status === "treatment_started",
    },
    {
        key: "retainers-issued",
        label: "Retainers Issued",
        sub: "Treatment complete",
        tone: "neutral",
        match: (c) => c.status === "completed",
    },
    {
        // iter97.88 — Cancelled is terminal but doctors still want a
        // searchable archive (refer back to old patient discussions,
        // billing reconciliation, etc.). Rose tone visually distinguishes
        // it from the neutral "Retainers Issued" card so the eye doesn't
        // confuse them.
        key: "cancelled",
        label: "Cancelled",
        sub: "Aborted before delivery",
        tone: "rose",
        match: (c) => c.status === "cancelled",
    },
];

/* Old bucket keys → new keys. Lets legacy bookmarks (and Dashboard tile links
   from the previous release) still land on a sensible filter. */
const LEGACY_STAGE_ALIAS = {
    "for-approval": "review-the-plan",
    "needs-input": "submitted-for-planning",
    "revising": "in-refinement",
    "submitted": "submitted-for-planning",
    "starting-soon": "submitted-to-lab",
    "ongoing": "aligners-issued",
    "paused": "aligners-issued",
    "completed": "retainers-issued",
    "all-active": null,
};

const TONE_CLASS = {
    neutral: {
        rail: "before:bg-foreground/20",
        dot: "bg-foreground/30",
        value: "text-foreground",
        chip: "bg-foreground/5 text-foreground/80 border-foreground/15",
    },
    "primary-soft": {
        rail: "before:bg-primary/50",
        dot: "bg-primary/50",
        value: "text-primary",
        chip: "bg-primary/5 text-primary border-primary/15",
    },
    primary: {
        rail: "before:bg-primary",
        dot: "bg-primary",
        value: "text-primary",
        chip: "bg-primary/10 text-primary border-primary/20",
    },
    amber: {
        rail: "before:bg-amber-500",
        dot: "bg-amber-500",
        value: "text-amber-700",
        chip: "bg-amber-100 text-amber-800 border-amber-200",
    },
    "amber-soft": {
        rail: "before:bg-amber-400",
        dot: "bg-amber-400",
        value: "text-amber-600",
        chip: "bg-amber-50 text-amber-700 border-amber-200",
    },
    // iter97.88 — Rose tone reserved for the terminal "Cancelled" bucket.
    // Distinct from the neutral terminal "Retainers Issued" so the eye
    // doesn't confuse "treatment completed successfully" with "aborted".
    rose: {
        rail: "before:bg-rose-400",
        dot: "bg-rose-400",
        value: "text-rose-700",
        chip: "bg-rose-50 text-rose-700 border-rose-200",
    },
};

const COLUMNS = [
    { key: "patient", label: "Patient (+ Case ID)" },
    { key: "status", label: "Status" },
    { key: "action_needed", label: "Action needed" },
    { key: "treatment_progress", label: "Treatment progress" },
    { key: "submitted_on", label: "Submitted on" },
];

/* iter98.99ap — Action-needed copy per case status. Surfaces the
   next-best-action for the doctor (or the party blocking the case)
   in plain English, replacing the old "stage chip + last activity"
   pair that doctors had to mentally translate.

   `pending` flag drives the row pill colour: amber = the doctor
   has work to do · slate = waiting on Invisique · emerald = case
   is closed or in steady-state. The text is intentionally short
   so the column stays scannable at common laptop widths. */
const ACTION_NEEDED_MAP = {
    draft:                   { text: "Resume drafting",              tone: "amber" },
    scan_visit_scheduled:    { text: "Awaiting technician visit",    tone: "slate" },
    scan_visit_completed:    { text: "Resume case wizard",           tone: "amber" },
    submitted:               { text: "Awaiting Invisique™",           tone: "slate" },
    in_planning:             { text: "Planner authoring",            tone: "slate" },
    plan_authoring:          { text: "Awaiting Invisique™",           tone: "slate" },
    submitted_to_qa:         { text: "Internal QA review",           tone: "slate" },
    pending_admin_review:    { text: "Awaiting admin review",        tone: "slate" },
    in_qa_review:            { text: "QA reviewing your plan",       tone: "slate" },
    sent_for_approval:       { text: "Review & approve plan",        tone: "amber" },
    ready_for_review:        { text: "Review & approve plan",        tone: "amber" },
    revision_requested:      { text: "Plan being reworked",          tone: "slate" },
    refinement_requested:    { text: "Refinement in progress",       tone: "slate" },
    info_requested:          { text: "Provide additional info",      tone: "amber" },
    approved:                { text: "Awaiting routing",              tone: "slate" },
    assigned_to_production:  { text: "Manufacturing queued",          tone: "slate" },
    submitted_to_production: { text: "Sent to lab",                   tone: "slate" },
    in_production:           { text: "Manufacturing in progress",     tone: "slate" },
    shipped:                 { text: "Confirm receipt at clinic",     tone: "amber" },
    in_treatment:            { text: "Schedule patient fitting",      tone: "amber" },
    treatment_started:       { text: "Monitor wear & check-ins",      tone: "slate" },
    completed:               { text: "Treatment complete",            tone: "emerald" },
    cancelled:               { text: "Case cancelled",                tone: "neutral" },
};

const ACTION_TONE_CLS = {
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    neutral: "bg-foreground/5 text-foreground/60 border-foreground/15",
};

function actionNeededFor(c) {
    return ACTION_NEEDED_MAP[c.status] || { text: "—", tone: "neutral" };
}

function daysSince(iso) {
    if (!iso) return Infinity;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return Infinity;
    return Math.floor((Date.now() - t) / 86400000);
}

function humanPatientId(patientId) {
    if (!patientId) return "—";
    return `INV-PT-${patientId.replace(/-/g, "").slice(0, 6).toUpperCase()}`;
}

function ago(iso) {
    if (!iso) return "—";
    const d = daysSince(iso);
    if (d === 0) return "today";
    if (d === 1) return "yesterday";
    if (d < 30) return `${d} days ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function stageOfCase(c) {
    const b = STAGE_BUCKETS.find((bucket) => bucket.key !== "all-active" && bucket.match(c));
    return b || null;
}

function progressOfCase(c) {
    const upper = c.plan?.aligner_count_upper || 0;
    const lower = c.plan?.aligner_count_lower || 0;
    const total = Math.max(upper, lower);
    const stage = c.current_aligner_stage || 0;
    const pct = total > 0 ? Math.min(100, Math.round((stage / total) * 100)) : 0;
    return { stage, total, pct };
}

export default function DoctorCases() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    // Tab — defaults to "list". `?tab=insights` from the Dashboard
    // "Compliance" section deep-links straight to the Aligner Insights view.
    const initialTab = searchParams.get("tab") === "insights" ? "insights" : "list";
    const [tab, setTabState] = useState(initialTab);
    const setTab = (next) => {
        setTabState(next);
        // Mirror the active tab into the URL so refresh / share keeps the
        // view. "list" is the default, so omit the param to keep URLs clean.
        const params = new URLSearchParams(searchParams);
        if (next === "list") params.delete("tab");
        else params.set("tab", next);
        setSearchParams(params, { replace: true });
    };

    // Filter / search / sort state — `stage` URL param seeds the active bucket so
    // the Dashboard's Action Needed tiles can deep-link straight to a filter.
    // Default is null = "show every case" (no card highlighted).
    const initialBucket = (() => {
        const raw = searchParams.get("stage");
        if (!raw) return null;
        // Legacy bucket keys redirect to their new equivalents
        const mapped = raw in LEGACY_STAGE_ALIAS ? LEGACY_STAGE_ALIAS[raw] : raw;
        if (!mapped) return null;
        return STAGE_BUCKETS.find((b) => b.key === mapped)?.key || null;
    })();
    const [activeBucket, setActiveBucket] = useState(initialBucket);
    const [query, setQuery] = useState("");
    const [idQuery, setIdQuery] = useState("");
    const [clinicFilter, setClinicFilter] = useState("all");
    const [sortKey, setSortKey] = useState("submitted_on");
    const [sortDir, setSortDir] = useState("desc");

    // Keep URL in sync when bucket changes (so the deep-link survives refresh
    // and the back button takes the doctor back to the unfiltered list cleanly).
    useEffect(() => {
        if (!activeBucket) {
            if (searchParams.get("stage")) {
                searchParams.delete("stage");
                setSearchParams(searchParams, { replace: true });
            }
        } else if (searchParams.get("stage") !== activeBucket) {
            searchParams.set("stage", activeBucket);
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeBucket]);

    // React to back/forward navigation that changes ?stage= externally.
    useEffect(() => {
        const raw = searchParams.get("stage");
        const mapped = raw && raw in LEGACY_STAGE_ALIAS ? LEGACY_STAGE_ALIAS[raw] : raw;
        const next = mapped ? (STAGE_BUCKETS.find((b) => b.key === mapped)?.key || null) : null;
        if (next !== activeBucket) setActiveBucket(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const load = async () => {
        setLoading(true);
        try {
            const c = await api.get("/cases");
            setCases(c.data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    // Workflow actions live on the dedicated case detail page now —
    // /doctor/cases/:caseId. The Aligner Dashboard is purely a list view.

    // Page now shows the full pipeline (pre-delivery + post-delivery + drafts).
    // iter90 — drafts are no longer filtered out; they live in the new
    // `drafts` stage bucket. Other buckets' match() functions never
    // include "draft" so the active-pipeline cards stay unaffected.
    const relevant = useMemo(() => cases, [cases]);
    // AlignerInsights donuts (wear / refinement / paused) only make sense for
    // post-delivery cases, so we filter again for that one component.
    const postDeliveryOnly = useMemo(() => cases.filter((c) => POST_DELIVERY.includes(c.status)), [cases]);

    // Bucket counts
    const bucketCounts = useMemo(() => {
        const out = {};
        for (const b of STAGE_BUCKETS) out[b.key] = relevant.filter(b.match).length;
        return out;
    }, [relevant]);

    // Unique clinics for filter — prefer the per-case practice_location_name,
    // fall back to the doctor-level clinic_name for legacy rows.
    const clinics = useMemo(() => {
        const set = new Set(
            relevant
                .map((c) => c.practice_location_name || c.clinic_name)
                .filter(Boolean)
        );
        return Array.from(set).sort();
    }, [relevant]);

    // Apply filters + search + sort
    const visibleCases = useMemo(() => {
        const bucket = STAGE_BUCKETS.find((b) => b.key === activeBucket);
        let list = bucket ? relevant.filter(bucket.match) : relevant;
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            list = list.filter((c) =>
                (c.patient_name || "").toLowerCase().includes(q) ||
                (c.case_code || "").toLowerCase().includes(q) ||
                (c.diagnosis || "").toLowerCase().includes(q));
        }
        if (idQuery.trim()) {
            // Search the visible "Case ID" column AND also match patient codes
            // so an admin who only has the patient code on hand can still find
            // the right row.
            const q = idQuery.trim().toLowerCase();
            list = list.filter((c) =>
                (c.code || "").toLowerCase().includes(q) ||
                humanPatientId(c.patient_id).toLowerCase().includes(q) ||
                (c.case_code || "").toLowerCase().includes(q));
        }
        if (clinicFilter !== "all") {
            list = list.filter((c) => (c.practice_location_name || c.clinic_name) === clinicFilter);
        }

        const lastActivityTs = (c) => new Date(c.aligner_stage_updated_at || c.updated_at || c.created_at || 0).getTime();
        const submittedTs = (c) => new Date(c.submitted_at || c.created_at || 0).getTime();
        const progressPct = (c) => {
            const upper = c.plan?.aligner_count_upper || 0;
            const lower = c.plan?.aligner_count_lower || 0;
            const total = Math.max(upper, lower);
            return total > 0 ? Math.min(100, Math.round(((c.current_aligner_stage || 0) / total) * 100)) : 0;
        };
        const dir = sortDir === "asc" ? 1 : -1;
        list = [...list].sort((a, b) => {
            switch (sortKey) {
                case "patient":            return dir * (a.patient_name || "").localeCompare(b.patient_name || "");
                case "status":             return dir * ((statusMetaFor(a.status).label || "").localeCompare(statusMetaFor(b.status).label || ""));
                case "action_needed":      return dir * (actionNeededFor(a).text.localeCompare(actionNeededFor(b).text));
                case "treatment_progress": return dir * (progressPct(a) - progressPct(b));
                case "submitted_on":       return dir * (submittedTs(a) - submittedTs(b));
                // Legacy fallback so URL-based bookmarks don't break.
                case "last_activity":      return dir * (lastActivityTs(a) - lastActivityTs(b));
                default:                   return dir * (submittedTs(a) - submittedTs(b));
            }
        });
        return list;
    }, [relevant, activeBucket, query, idQuery, clinicFilter, sortKey, sortDir]);

    const onSort = (key) => {
        if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir("desc"); }
    };

    return (
        <div data-testid="my-cases-page" className="space-y-8">
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">My Cases</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Track every patient from impressions to retention. Open a case to review records, plan, and treatment progress.
                    </p>
                </div>
                <Button className="rounded-full h-11 px-5" onClick={() => navigate("/doctor/cases/new")} data-testid="new-case-btn">
                    <Plus size={16} className="mr-1.5" weight="bold" /> New Case
                </Button>
            </div>

            {/* SEGMENTED TAB CONTROL — paired info cards that double as the
                primary nav for this page. Earlier we used a thin underline
                strip and the Insights tab was invisible to most doctors;
                this larger card-pair surfaces both the count and the
                purpose of each view so neither gets missed. */}
            {/* Visual upgrade (iter 56): the previous flat card-pair felt
                static — every screen full of medical data looks the same. The
                new treatment gives each tab a clear "selected" identity:
                gradient top-bar (like a folder tab), elevated shadow, primary
                ring, gradient icon disc. Inactive tabs get a quieter chrome
                that visibly lifts on hover (-translate-y-0.5 + ring) so the
                pair feels like a single connected control surface. */}
            <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-1.5 rounded-2xl bg-gradient-to-br from-secondary/60 to-secondary/30 border border-border/80"
                role="tablist"
                data-testid="aligner-tabbar"
            >
                <button
                    role="tab"
                    aria-selected={tab === "list"}
                    onClick={() => setTab("list")}
                    data-testid="tab-cases"
                    className={`group relative flex items-start gap-3.5 text-left rounded-xl px-4 py-4 transition-all duration-200 overflow-hidden ${
                        tab === "list"
                            ? "bg-card ring-1 ring-primary/30 shadow-lg shadow-primary/5"
                            : "ring-1 ring-transparent hover:bg-card/70 hover:ring-border hover:-translate-y-0.5 hover:shadow-md"
                    }`}
                >
                    {/* Active tab indicator — gradient bar across the top.
                        Reads as "this tab is selected" at peripheral glance. */}
                    {tab === "list" && (
                        <span className="absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gradient-to-r from-primary/0 via-primary to-primary/0" aria-hidden="true" />
                    )}
                    <div className={`mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 transition-all duration-200 ${
                        tab === "list"
                            ? "bg-gradient-to-br from-primary to-primary/70 text-white shadow-md shadow-primary/30"
                            : "bg-card text-muted-foreground ring-1 ring-border/60 group-hover:text-primary group-hover:ring-primary/30"
                    }`}>
                        <ListBullets size={20} weight={tab === "list" ? "fill" : "duotone"} className="transition-transform group-hover:scale-110" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-display text-base font-bold ${tab === "list" ? "text-foreground" : "text-muted-foreground group-hover:text-foreground transition-colors"}`}>
                                Cases
                            </span>
                            <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 transition-colors ${
                                tab === "list"
                                    ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                                    : "bg-muted/80 text-muted-foreground"
                            }`} data-testid="tab-cases-count">
                                {relevant.length} total
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 leading-snug">
                            All patients on treatment — filter, search, or open a chart.
                        </div>
                    </div>
                </button>

                <button
                    role="tab"
                    aria-selected={tab === "insights"}
                    onClick={() => setTab("insights")}
                    data-testid="tab-insights"
                    className={`group relative flex items-start gap-3.5 text-left rounded-xl px-4 py-4 transition-all duration-200 overflow-hidden ${
                        tab === "insights"
                            ? "bg-card ring-1 ring-primary/30 shadow-lg shadow-primary/5"
                            : "ring-1 ring-transparent hover:bg-card/70 hover:ring-border hover:-translate-y-0.5 hover:shadow-md"
                    }`}
                >
                    {tab === "insights" && (
                        <span className="absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gradient-to-r from-primary/0 via-primary to-primary/0" aria-hidden="true" />
                    )}
                    <div className={`mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 transition-all duration-200 ${
                        tab === "insights"
                            ? "bg-gradient-to-br from-primary to-primary/70 text-white shadow-md shadow-primary/30"
                            : "bg-card text-muted-foreground ring-1 ring-border/60 group-hover:text-primary group-hover:ring-primary/30"
                    }`}>
                        <ChartDonut size={20} weight={tab === "insights" ? "fill" : "duotone"} className="transition-transform group-hover:scale-110" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-display text-base font-bold ${tab === "insights" ? "text-foreground" : "text-muted-foreground group-hover:text-foreground transition-colors"}`}>
                                Compliance Insights
                            </span>
                            <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 transition-colors ${
                                tab === "insights"
                                    ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                                    : "bg-muted/80 text-muted-foreground"
                            }`} data-testid="tab-insights-count">
                                {postDeliveryOnly.length} monitoring
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 leading-snug">
                            Wear-time, check-in &amp; refinement risk across post-delivery cases.
                        </div>
                    </div>
                </button>
            </div>

            {tab === "insights" && (
                loading
                    ? <div className="bg-card border border-border rounded-2xl p-10 text-center text-sm text-muted-foreground">Loading insights…</div>
                    : <AlignerInsights cases={postDeliveryOnly} />
            )}

            {tab === "list" && (
                <>
            {/* STAGE CARDS — 7 workflow milestones doctors actually talk about
                (incl. iter90 Drafts as the first card). */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stage-cards">
                {STAGE_BUCKETS.map((b) => (
                    <StageCard
                        key={b.key}
                        bucket={b}
                        count={bucketCounts[b.key] || 0}
                        isActive={activeBucket === b.key}
                        onClick={() => setActiveBucket((prev) => (prev === b.key ? null : b.key))}
                    />
                ))}
            </div>

            {/* Active filter banner — only shows when a bucket is selected */}
            {activeBucket && (
                <div
                    className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-primary/5 border border-primary/20 text-sm"
                    data-testid="active-filter-banner"
                >
                    <span className="text-xs font-bold tracking-wider uppercase text-primary">Filtered</span>
                    <span className="text-foreground">
                        Showing{" "}
                        <span className="font-semibold">
                            {STAGE_BUCKETS.find((b) => b.key === activeBucket)?.label}
                        </span>
                        {" "}only
                    </span>
                    <button
                        onClick={() => setActiveBucket(null)}
                        className="ml-auto text-xs text-primary font-semibold hover:underline"
                        data-testid="clear-stage-filter"
                    >
                        Show all cases ×
                    </button>
                </div>
            )}

            {/* TABLE */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {/* Column header — iter98.99ap consolidated 5 columns:
                    Patient (+ Case ID) · Status · Action needed · Treatment progress · Submitted on. */}
                <div className="hidden md:grid grid-cols-[1.6fr_1fr_1.2fr_1.2fr_0.9fr_30px] gap-3 px-5 py-3 bg-secondary/40 border-b border-border text-[11px] font-bold tracking-[0.15em] uppercase text-foreground/70">
                    {COLUMNS.map((col) => (
                        <button
                            key={col.key}
                            onClick={() => onSort(col.key)}
                            className="flex items-center gap-1 hover:text-foreground transition-colors text-left"
                            data-testid={`col-${col.key}`}
                        >
                            {col.label}
                            {sortKey === col.key
                                ? (sortDir === "asc"
                                    ? <CaretUp size={10} weight="bold" />
                                    : <CaretDown size={10} weight="bold" />)
                                : <CaretUpDown size={10} className="opacity-30" />
                            }
                        </button>
                    ))}
                    <div />
                </div>

                {/* Filter row — patient/id search collapsed into the first
                    column, clinic filter retained alongside, the other 3
                    columns get static hints so the row keeps its rhythm. */}
                <div className="hidden md:grid grid-cols-[1.6fr_1fr_1.2fr_1.2fr_0.9fr_30px] gap-3 px-5 py-3 border-b border-border">
                    <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Patient..."
                                className="h-9 pl-7 text-sm"
                                data-testid="filter-patient"
                            />
                        </div>
                        <div className="relative">
                            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={idQuery}
                                onChange={(e) => setIdQuery(e.target.value)}
                                placeholder="Case ID..."
                                className="h-9 pl-7 text-sm"
                                data-testid="filter-id"
                            />
                        </div>
                    </div>
                    <Select value={clinicFilter} onValueChange={setClinicFilter}>
                        <SelectTrigger className="h-9 text-sm" data-testid="filter-clinic">
                            <SelectValue placeholder="All clinics" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All clinics</SelectItem>
                            {clinics.map((cl) => <SelectItem key={cl} value={cl}>{cl}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <div className="h-9 flex items-center text-xs text-muted-foreground italic px-2">Filter by stage above</div>
                    <div className="h-9 flex items-center text-xs text-muted-foreground italic px-2">Sort by column header</div>
                    <div className="h-9 flex items-center text-xs text-muted-foreground italic px-2">Newest first</div>
                    <div />
                </div>

                {/* Data rows */}
                {loading && <div className="p-10 text-center text-muted-foreground text-sm">Loading cases…</div>}
                {!loading && relevant.length === 0 && (
                    <div className="p-12 text-center" data-testid="aligner-empty-state">
                        <Cube size={32} weight="duotone" className="mx-auto mb-2 text-muted-foreground opacity-40" />
                        <p className="text-muted-foreground text-sm mb-4">No cases in active treatment yet.<br />Cases appear here once they are approved and on their way to the patient.</p>
                        <Button onClick={() => navigate("/doctor/cases/new")} size="sm" className="rounded-full">
                            <Plus size={14} className="mr-1" /> New Case
                        </Button>
                    </div>
                )}
                {!loading && relevant.length > 0 && visibleCases.length === 0 && (
                    <div className="p-10 text-center text-sm text-muted-foreground" data-testid="aligner-no-match">
                        No cases match this filter. <button onClick={() => { setActiveBucket(null); setQuery(""); setIdQuery(""); setClinicFilter("all"); }} className="text-primary font-semibold underline">Clear filters</button>
                    </div>
                )}

                <div className="divide-y divide-border" data-testid="aligner-table-body">
                    {visibleCases.map((c) => (
                        <CaseTableRow key={c.id} c={c} />
                    ))}
                </div>

                {!loading && visibleCases.length > 0 && (
                    <div className="px-5 py-3 border-t border-border text-xs text-muted-foreground bg-secondary/30">
                        Showing <span className="font-semibold text-foreground">{visibleCases.length}</span> of {relevant.length} cases
                    </div>
                )}
            </div>
                </>
            )}
        </div>
    );
}

/* ─────────── Stage filter card ─────────── */
function StageCard({ bucket, count, isActive, onClick }) {
    const t = TONE_CLASS[bucket.tone];
    return (
        <button
            onClick={onClick}
            data-testid={`stage-card-${bucket.key}`}
            className={`text-left p-4 rounded-2xl border transition-all ${
                isActive
                    ? "bg-primary/5 border-primary ring-1 ring-primary/30"
                    : "bg-card border-border hover:border-primary/40 hover:-translate-y-0.5 hover:shadow-sm"
            }`}
        >
            <div className="flex items-start justify-between gap-2 mb-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground leading-tight min-h-[24px]">
                    {bucket.label}
                </span>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1 ${t.dot}`} />
            </div>
            <div className={`font-display text-4xl font-bold tabular-nums leading-none ${t.value}`}>
                {count}
            </div>
            <div className="text-[11px] text-muted-foreground mt-3 leading-snug min-h-[28px]">
                {bucket.sub}
            </div>
        </button>
    );
}


/* ─────────── Case row — clicking navigates to the dedicated workspace.
   iter98.99ap — Consolidated to 5 columns:
     1. Patient (+ Case ID)  — avatar · name · diagnosis · case code · patient code
     2. Status               — current status pill from statusMetaFor()
     3. Action needed        — what the doctor / Invisique side needs to do next
     4. Treatment progress   — aligner X/Y with mini progress bar (when relevant)
     5. Submitted on         — submitted_at (fallback: created_at)
*/
function CaseTableRow({ c }) {
    const stage = stageOfCase(c);
    const statusMeta = statusMetaFor(c.status);
    const statusTone = TONE_CLASS[statusMeta.tone] || TONE_CLASS.neutral;
    const action = actionNeededFor(c);
    const progress = progressOfCase(c);
    const submittedIso = c.submitted_at || c.created_at;
    // iter90 — drafts deep-link back to the wizard at the right step,
    // not to the read-only CaseDetail (which would be useless for a
    // case that doesn't have submission data yet). Other statuses
    // continue routing to CaseDetail as before.
    const isDraft = c.status === "draft";
    const targetHref = isDraft
        ? `/doctor/cases/new?draft=${c.id}&step=${computeResumeStep(c)}`
        : `/doctor/cases/${c.id}`;

    return (
        <Link
            to={targetHref}
            data-testid={`case-row-${c.id}`}
            className="block w-full text-left grid grid-cols-1 md:grid-cols-[1.6fr_1fr_1.2fr_1.2fr_0.9fr_30px] gap-3 px-5 py-4 hover:bg-secondary/40 transition-colors"
        >
            {/* Patient (+ Case ID) — name + diagnosis on top, case code +
                patient code in a smaller secondary line. Single column
                instead of two so doctors don't have to triangulate. */}
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
                    {(c.patient_name || "P").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{c.patient_name || "—"}</span>
                        {c.is_demo && <DemoBadge />}
                        {/* iter100.73 — Red pulsing dot when compliance mode is unset
                            on an active (non-terminal, non-draft) case. Nudges the
                            doctor to click into the row and pick a mode. */}
                        {c.coe_support_compliance == null && !["draft", "completed", "cancelled"].includes(c.status) && (
                            <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-700 uppercase tracking-wider"
                                title="Compliance mode not chosen — click into the case to pick"
                                data-testid={`case-row-compliance-unset-${c.id}`}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-compliance-pulse" aria-hidden />
                                Compliance
                            </span>
                        )}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground flex items-center gap-1.5 truncate" data-testid="cases-row-code">
                        <span className="font-mono text-foreground/80">{c.code || "—"}</span>
                        <span className="text-muted-foreground/50">·</span>
                        <span className="truncate">{humanPatientId(c.patient_id)}</span>
                    </div>
                </div>
            </div>

            {/* Status — single canonical pill from statusMetaFor(). */}
            <div className="md:flex md:items-center min-w-0">
                <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider text-[10px] ${statusTone.chip}`}
                    data-testid={`case-status-${c.id}`}
                >
                    {statusMeta.label}
                </span>
            </div>

            {/* Action needed — short next-best-action pill. */}
            <div className="md:flex md:items-center min-w-0">
                <span
                    className={`inline-flex items-center max-w-full px-2 py-1 rounded-md border text-[11px] font-medium truncate ${ACTION_TONE_CLS[action.tone] || ACTION_TONE_CLS.slate}`}
                    title={action.text}
                    data-testid={`case-action-${c.id}`}
                >
                    {action.text}
                </span>
            </div>

            {/* Treatment progress — for drafts we show wizard progress
                (X of 6 steps done · Step Y next). For live cases we show
                aligner progress. Otherwise a quiet em-dash so the row
                doesn't look broken. iter101.76 — Draft rows previously
                showed just "—" here; doctors couldn't tell which draft
                was almost done vs. barely started without opening each
                one. iter101.77 — Near-complete drafts (5/6 or 6/6) flip
                the bar to emerald so the "almost done" story reads even
                without opening the row. */}
            <div className="md:flex md:flex-col md:justify-center min-w-0" data-testid={`case-progress-${c.id}`}>
                {isDraft ? (() => {
                    const resumeStep = computeResumeStep(c);
                    // iter101.83 — Honest progress count regardless of
                    // step order (see computeDraftProgress docstring).
                    const { done: stepsDone, total: totalSteps } = computeDraftProgress(c);
                    const pct = Math.round((stepsDone / totalSteps) * 100);
                    const nextLabel = resumeStep > totalSteps
                        ? "Review & Submit"
                        : `Step ${resumeStep}`;
                    const almostReady = stepsDone >= totalSteps - 1;
                    const barTone = almostReady ? "bg-emerald-500" : "bg-amber-500";
                    return (
                        <>
                            <div className="flex items-center gap-1.5 text-[11px] text-foreground/80 tabular-nums">
                                <span className={`font-semibold ${almostReady ? "text-emerald-700" : ""}`}>{stepsDone}/{totalSteps}</span>
                                <span className="text-muted-foreground truncate">steps · {nextLabel} next</span>
                            </div>
                            <div className="w-full bg-secondary rounded-full h-1.5 mt-1">
                                <div
                                    className={`h-1.5 rounded-full transition-all ${barTone}`}
                                    style={{ width: `${pct}%` }}
                                    data-testid={`draft-progress-bar-${c.id}`}
                                />
                            </div>
                        </>
                    );
                })() : progress.total > 0 ? (
                    <>
                        <div className="flex items-center gap-2 text-[11px] text-foreground/80 tabular-nums">
                            <span className="font-semibold">{progress.stage}/{progress.total}</span>
                            <span className="text-muted-foreground">aligner</span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-1.5 mt-1">
                            <div
                                className="h-1.5 rounded-full bg-primary transition-all"
                                style={{ width: `${progress.pct}%` }}
                            />
                        </div>
                    </>
                ) : (
                    <span className="text-xs text-muted-foreground/60">—</span>
                )}
            </div>

            {/* Submitted on — submitted_at (fallback: created_at). */}
            <div className="text-xs md:flex md:flex-col md:justify-center min-w-0">
                <span className="text-foreground/80">{formatDate(submittedIso)}</span>
                {submittedIso && (
                    <span className="text-[10.5px] text-muted-foreground/80">{ago(submittedIso)}</span>
                )}
            </div>

            {/* Open chevron */}
            <div className="hidden md:flex items-center justify-end">
                <CaretRight size={14} className="text-muted-foreground" />
            </div>
        </Link>
    );
}

